import { type FunctionComponent, useEffect } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { type EditorState, type LexicalEditor } from "lexical";

import { type RichTextEditorSerializeAs } from "../RichTextEditorProps";
import { serializeComponents } from "../utils/componentsSerialization";
import { serializeHtml } from "../utils/htmlSerialization";
import { serializeMarkdown } from "../utils/markdownSerialization";

export interface SerializeOnChangePluginDefs {
  readonly serializeAs: RichTextEditorSerializeAs;
  readonly onChange: (value: string) => void;
}

const serializers: Record<RichTextEditorSerializeAs, (editor: LexicalEditor, editorState: EditorState) => string> = {
  html: (editor) => serializeHtml(editor),
  markdown: () => serializeMarkdown(),
  "lexical-json": (_editor, editorState) => JSON.stringify(editorState.toJSON()),
  components: () => serializeComponents(),
};

export const SerializeOnChangePlugin: FunctionComponent<SerializeOnChangePluginDefs> = ({ serializeAs, onChange }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
      editorState.read(() => onChange(serializers[serializeAs](editor, editorState)));
    });
  }, [editor, serializeAs, onChange]);
  return null;
};
